import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";

const CATS = ["contact", "followup", "selftape", "materials", "networking", "research", "meeting", "other"];
const STATUSES = ["todo", "in_progress", "done", "postponed"];

export default function TaskFormDialog({ open, onOpenChange, contacts, task, onSaved }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const isEdit = !!task;
  const [form, setForm] = useState({
    title: "", category: "followup", status: "todo",
    due: "", notes: "", contact_id: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        title: task.title || "",
        category: task.category || "other",
        status: task.status || "todo",
        due: (task.due || "").slice(0, 10),
        notes: task.notes || "",
        contact_id: task.contact_id || "",
      });
    } else {
      setForm({ title: "", category: "followup", status: "todo", due: "", notes: "", contact_id: "" });
    }
  }, [open, task]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) return;
    const payload = {
      title: form.title.trim(),
      category: form.category,
      status: form.status,
      due: form.due || null,
      notes: form.notes.trim(),
      contact_id: form.contact_id || null,
    };
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/ops/tasks/${task.task_id}`, payload);
      else await api.post("/ops/tasks", payload);
      toast.success(t("ops.form.saved"));
      onOpenChange?.(false);
      onSaved?.();
    } catch { toast.error(t("ops.form.fail")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-form-dialog"
        className="max-w-xl bg-stone-950 border-stone-800 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-stone-100">
            {isEdit ? t("ops.form.title_edit") : t("ops.form.title_new")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <label className="label-cap">{t("ops.form.name")}</label>
            <input data-testid="task-name" required value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder={t("ops.form.name_ph")} className="script-input w-full"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-cap">{t("ops.form.category")}</label>
              <select data-testid="task-cat" value={form.category}
                onChange={(e) => set("category", e.target.value)} className="script-input w-full">
                {CATS.map((c) => <option key={c} value={c}>{t(`ops.cat.${c}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="label-cap">{t("crm.col.status")}</label>
              <select data-testid="task-status" value={form.status}
                onChange={(e) => set("status", e.target.value)} className="script-input w-full">
                {STATUSES.map((s) => <option key={s} value={s}>{t(`ops.status.${s}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-cap">{t("ops.form.due")}</label>
              <input data-testid="task-due" type="date" value={form.due}
                onChange={(e) => set("due", e.target.value)} className="script-input w-full" />
            </div>
            <div>
              <label className="label-cap">{t("ops.form.contact")}</label>
              <select data-testid="task-contact" value={form.contact_id}
                onChange={(e) => set("contact_id", e.target.value)}
                className="script-input w-full">
                <option value="">{t("ops.form.contact_none")}</option>
                {contacts?.map((c) => (
                  <option key={c.contact_id} value={c.contact_id}>
                    {c.first_name} {c.last_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label-cap">{t("ops.form.notes")}</label>
            <textarea data-testid="task-notes" value={form.notes}
              onChange={(e) => set("notes", e.target.value)} rows={3}
              className="script-input w-full resize-none" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange?.(false)} className="ghost-btn">
              {t("action.cancel")}
            </button>
            <button type="submit" data-testid="task-save" disabled={saving} className="pill-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {t("ops.form.save")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
